import { styled, Typography, Button, Box } from '@mui/material';
import { IconXClose } from '@shared/assets/images/svgs';
import { t } from 'i18next';
import React from 'react';
import { ToastContentProps } from 'react-toastify';

const ToastContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  minWidth: '320px',
  maxWidth: '480px',
  gap: theme.spacing(1.5),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '42px',
  height: '42px',
  minWidth: '42px',
  backgroundColor: theme.palette.error.light,
  borderRadius: '4px',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
}));

const CloseButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '10px',
  top: '10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  textTransform: 'none',
  fontSize: '12px',
  padding: theme.spacing(0.5, 1.5),
  alignSelf: 'flex-start',
}));

export interface CustomErrorToastProps extends ToastContentProps {
  message?: React.ReactNode;
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
  suggestion?: string;
  technical?: boolean;
}

export const CustomErrorToast = ({
  closeToast,
  message,
  title,
  onRetry,
  retryLabel,
  suggestion,
  technical = false,
}: CustomErrorToastProps): React.ReactElement => {
  const errorTitle = title || t('common.error');
  const errorMessage = message ?? t('common.errorMessage');

  return (
    <>
      <ToastContainer>
        <IconWrapper>
          <Box
            component="span"
            sx={{
              color: 'error.main',
              fontSize: '24px',
              fontWeight: 'bold',
              lineHeight: 1,
            }}>
            !
          </Box>
        </IconWrapper>
        <ContentWrapper>
          <Typography
            variant="subtitle2"
            color="text.primary"
            fontSize="14px"
            fontWeight="600"
            sx={{ lineHeight: 1.4 }}>
            {errorTitle}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="13px"
            sx={{ lineHeight: 1.4, wordBreak: 'break-word' }}>
            {errorMessage}
          </Typography>
          {suggestion && !technical && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontSize="12px"
              sx={{
                lineHeight: 1.4,
                fontStyle: 'italic',
                opacity: 0.8,
              }}>
              💡 {suggestion}
            </Typography>
          )}
          {onRetry && (
            <ActionButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                onRetry();
                closeToast?.();
              }}>
              {retryLabel || t('common.retry')}
            </ActionButton>
          )}
        </ContentWrapper>
      </ToastContainer>
      <CloseButton onClick={() => closeToast?.()}>
        <IconXClose />
      </CloseButton>
    </>
  );
};
